<script>
import { mapGetters, mapActions } from 'vuex'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

export default {
  name: 'InstructionalTooltip',

  data () {
    return {
      targetPosition: null,
      isVisible: false,
      targetElement: null, // Armazena referência do elemento para limpeza
      resizeHandler: null, // Para armazenar referência do listener
      scrollHandler: null, // Para armazenar referência do scroll listener
      showTimeoutId: null,
      scrollTimeoutId: null,
      keydownHandler: null,
    }
  },

  computed: {
    ...mapGetters('userTips', ['getCurrentTip', 'isShowingTip']),

    currentTip () {
      return this.getCurrentTip
    },

    title () {
      return this.currentTip?.title || ''
    },

    content () {
      return this.currentTip?.content || ''
    },

    position () {
      return this.currentTip?.position || 'right'
    },

    spotlightStyle () {
      if (!this.targetPosition) return {}

      return {
        top: `${this.targetPosition.top - 8}px`,
        left: `${this.targetPosition.left - 8}px`,
        width: `${this.targetPosition.width + 16}px`,
        height: `${this.targetPosition.height + 16}px`,
      }
    },

    tooltipStyle () {
      if (!this.targetPosition) return {}

      const isMobile = window.innerWidth <= 768
      const offset = 20
      let top = this.targetPosition.top
      let left = this.targetPosition.left

      if (isMobile) {
        // Em mobile, centraliza horizontalmente abaixo do elemento
        const horizontalPadding = 16
        const availableWidth = Math.max(window.innerWidth - (horizontalPadding * 2), 200)
        const tooltipWidth = Math.min(280, availableWidth) // Largura do tooltip
        left = this.targetPosition.left + (this.targetPosition.width / 2) - (tooltipWidth / 2)
        top = this.targetPosition.top + this.targetPosition.height + offset

        // Garante que não saia da tela (horizontal)
        const maxLeft = window.innerWidth - tooltipWidth - horizontalPadding
        const minLeft = horizontalPadding

        if (left > maxLeft) {
          left = maxLeft
        }
        if (left < minLeft) {
          left = minLeft
        }

        // Garante que o tooltip não saia pela parte inferior
        const tooltipHeight = 200 // Altura estimada do tooltip
        const maxTop = window.innerHeight - tooltipHeight - 20

        if (top > maxTop) {
          // Se não cabe embaixo, posiciona acima
          top = this.targetPosition.top - offset - tooltipHeight
          if (top < 20) {
            // Se não cabe acima também, centraliza verticalmente
            top = (window.innerHeight - tooltipHeight) / 2
          }
        }
      } else {
        // Lógica atual para desktop
        switch (this.position) {
          case 'right':
            left = this.targetPosition.left + this.targetPosition.width + offset
            top = this.targetPosition.top + (this.targetPosition.height / 2)
            break
          case 'left':
            left = this.targetPosition.left - offset
            top = this.targetPosition.top + (this.targetPosition.height / 2)
            break
          case 'bottom':
            left = this.targetPosition.left + (this.targetPosition.width / 2)
            top = this.targetPosition.top + this.targetPosition.height + offset
            break
          case 'top':
            left = this.targetPosition.left + (this.targetPosition.width / 2)
            top = this.targetPosition.top - offset
            break
        }
      }

      return {
        top: `${top}px`,
        left: `${left}px`,
      }
    },
  },

  watch: {
    currentTip: {
      immediate: true,
      handler (newTip) {
        if (newTip && newTip.targetRef) {
          this.showTooltip(newTip)
        } else {
          this.hideTooltip()
        }
      },
    },

    isVisible: {
      handler (isVisible) {
        if (isVisible) {
          disableBodyScroll(document.body, {
            reserveScrollBarGap: true,
          })
        } else {
          enableBodyScroll(document.body)
        }
      },
    },
  },

  methods: {
    ...mapActions('userTips', ['hideTip']),

    clearShowTimeout () {
      if (this.showTimeoutId) {
        clearTimeout(this.showTimeoutId)
        this.showTimeoutId = null
      }
    },

    clearScrollTimeout () {
      if (this.scrollTimeoutId) {
        clearTimeout(this.scrollTimeoutId)
        this.scrollTimeoutId = null
      }
    },

    resolveElement (element) {
      if (!element) return null
      return element.$el || element
    },

    isElementAvailable (element) {
      if (!element) return false
      return element.isConnected && document.body.contains(element)
    },

    handleMissingTarget (tip) {
      if (tip?.key) {
        this.hideTip({ tipKey: tip.key, markAsSeen: false })
      } else {
        this.hideTooltip()
      }
    },

    showTooltip (tip) {
      const delay = tip.delay || 1000

      this.clearShowTimeout()
      this.showTimeoutId = setTimeout(() => {
        const element = this.resolveElement(tip.targetRef)
        if (this.isElementAvailable(element)) {
          // Armazena referência do elemento
          this.targetElement = element

          // Verifica se o elemento está visível na viewport
          const rect = element.getBoundingClientRect()
          const isElementVisible = (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= window.innerHeight &&
            rect.right <= window.innerWidth
          )

          if (!isElementVisible) {
            // Elemento não está visível - fazer scroll automático
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            })

            // Aguardar scroll completar antes de mostrar tooltip
            this.clearScrollTimeout()
            this.scrollTimeoutId = setTimeout(() => {
              if (this.isElementAvailable(element)) {
                this.calculateAndShowTooltip(element)
              } else {
                this.handleMissingTarget(tip)
              }
            }, 800)
          } else {
            // Elemento está visível - mostrar tooltip direto
            this.calculateAndShowTooltip(element)
          }
        } else {
          this.handleMissingTarget(tip)
        }
      }, delay)
    },

    calculateAndShowTooltip (element) {
      if (!this.isElementAvailable(element)) {
        this.handleMissingTarget(this.currentTip)
        return
      }

      const rect = element.getBoundingClientRect()
      this.targetPosition = {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
      }
      this.isVisible = true
      this.setupKeydownListener()

      // Adiciona classe ao elemento para evitar cliques durante a instrução
      element.style.pointerEvents = 'none'

      // Adiciona listeners para redimensionamento e scroll
      this.setupResizeListener(element)
      this.setupScrollListener(element)
    },

    setupResizeListener (element) {
      // Remove listener anterior se existir
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
      }

      // Cria novo listener
      this.resizeHandler = () => {
        if (this.isVisible) {
          if (!this.isElementAvailable(element)) {
            this.handleMissingTarget(this.currentTip)
            return
          }
          // Recalcula posição quando a janela é redimensionada
          const rect = element.getBoundingClientRect()
          this.targetPosition = {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height,
          }
        }
      }

      // Adiciona o listener
      window.addEventListener('resize', this.resizeHandler)
    },

    setupScrollListener (element) {
      // Remove listener anterior se existir
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler)
      }

      // Cria novo listener para scroll
      this.scrollHandler = () => {
        if (this.isVisible) {
          if (!this.isElementAvailable(element)) {
            this.handleMissingTarget(this.currentTip)
            return
          }
          // Recalcula posição quando a página é rolada
          const rect = element.getBoundingClientRect()
          this.targetPosition = {
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height,
          }
        }
      }

      // Adiciona o listener
      window.addEventListener('scroll', this.scrollHandler)
    },

    setupKeydownListener () {
      if (this.keydownHandler) return

      this.keydownHandler = (event) => {
        const isEscape = event.key === 'Escape' || event.keyCode === 27
        if (isEscape && this.isVisible) {
          event.preventDefault()
          event.stopPropagation()
          if (event.stopImmediatePropagation) {
            event.stopImmediatePropagation()
          }
          // Fecha o tooltip ao pressionar a tecla 'Esc'
          this.handleUnderstood()
        }
      }

      window.addEventListener('keydown', this.keydownHandler, true)
    },

    removeKeydownListener () {
      if (this.keydownHandler) {
        window.removeEventListener('keydown', this.keydownHandler, true)
        this.keydownHandler = null
      }
    },

    removeResizeListener () {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },

    removeScrollListener () {
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler)
        this.scrollHandler = null
      }
    },

    removeAllListeners () {
      this.removeResizeListener()
      this.removeScrollListener()
      this.removeKeydownListener()
    },

    hideTooltip () {
      this.clearShowTimeout()
      this.clearScrollTimeout()
      // Remove todos os listeners
      this.removeAllListeners()

      // Remove estilo do elemento ANTES de limpar tudo
      if (this.targetElement) {
        this.targetElement.style.pointerEvents = ''
        this.targetElement = null
      }

      this.isVisible = false
      this.targetPosition = null
    },

    handleUnderstood () {
      if (this.currentTip) {
        this.hideTip({ tipKey: this.currentTip.key, markAsSeen: true })
      }
    },
  },

  beforeDestroy () {
    this.removeAllListeners()
    this.hideTooltip()
    enableBodyScroll(document.body)
  },
}
</script>

<template>
  <transition name="fade">
    <div v-if="isVisible" :class="$classes['instructional-tooltip-wrapper']">
      <!-- Overlay escuro -->
      <div :class="$classes['instructional-overlay']"></div>

      <!-- Spotlight (área destacada) -->
      <div v-if="targetPosition" :class="$classes['instructional-spotlight']" :style="spotlightStyle">
        <!-- Animação de pulso -->
        <div :class="$classes['pulse-animation']"></div>
      </div>

      <!-- Tooltip com conteúdo -->
      <div v-if="targetPosition" :class="[$classes['instructional-tooltip'], $classes[`position-${position}`]]"
        :style="tooltipStyle">
        <div :class="$classes['tooltip-arrow']"></div>
        <div :class="$classes['tooltip-content']">
          <div v-if="title" :class="$classes['tooltip-title']">
            {{ title }}
          </div>
          <div :class="$classes['tooltip-body']">
            {{ content }}
          </div>
          <div :class="$classes['tooltip-actions']">
            <button :class="$classes['btn-understood']" @click="handleUnderstood">
              Entendi
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style module>
/* Wrapper principal */
.instructional-tooltip-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  pointer-events: none;
}

/* Overlay escuro */
.instructional-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  pointer-events: all;
  animation: fadeIn 0.3s ease-in-out;
}

/* Spotlight (área destacada) */
.instructional-spotlight {
  position: absolute;
  background-color: transparent;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
  z-index: 10000;
  pointer-events: none;
  transition: all 0.3s ease-in-out;
}

/* Animação de pulso */
.pulse-animation {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 2px solid #4A94D4;
  border-radius: 8px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }

  50% {
    transform: scale(1.05);
    opacity: 0.7;
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Tooltip */
.instructional-tooltip {
  position: absolute;
  z-index: 10001;
  pointer-events: all;
  animation: slideIn 0.3s ease-out 0.3s both;
}

/* Posicionamento do tooltip */
.position-right {
  transform: translateY(-50%);
}

.position-left {
  transform: translate(-100%, -50%);
}

.position-bottom {
  transform: translateX(-50%);
}

.position-top {
  transform: translate(-50%, -100%);
}

/* Seta do tooltip */
.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
}

.position-right .tooltip-arrow {
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-right: 10px solid #fff;
}

.position-left .tooltip-arrow {
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid #fff;
}

.position-bottom .tooltip-arrow {
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 10px solid #fff;
}

.position-top .tooltip-arrow {
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #fff;
}

/* Conteúdo do tooltip */
.tooltip-content {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 320px;
  min-width: 250px;
}

.tooltip-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.tooltip-title::before {
  content: '💡';
  margin-right: 8px;
  font-size: 20px;
}

.tooltip-body {
  white-space: pre-line;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin-bottom: 16px;
}

/* Ações */
.tooltip-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn-understood {
  background-color: #4A94D4;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-understood:hover {
  background-color: #456e92;
}

.btn-understood:active {
  transform: scale(0.98);
}

/* Animações */
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50%) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(-50%) translateY(0);
  }
}

.position-left.instructional-tooltip {
  animation: slideInLeft 0.3s ease-out 0.3s both;
}

.position-bottom.instructional-tooltip {
  animation: slideInBottom 0.3s ease-out 0.3s both;
}

.position-top.instructional-tooltip {
  animation: slideInTop 0.3s ease-out 0.3s both;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translate(-100%, -50%) translateX(10px);
  }

  to {
    opacity: 1;
    transform: translate(-100%, -50%) translateX(0);
  }
}

@keyframes slideInBottom {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes slideInTop {
  from {
    opacity: 0;
    transform: translate(-50%, -100%) translateY(10px);
  }

  to {
    opacity: 1;
    transform: translate(-50%, -100%) translateY(0);
  }
}

/* Transição de fade */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Responsividade */
@media (max-width: 768px) {
  .tooltip-content {
    max-width: 280px;
    min-width: 250px;
    padding: 16px;
    font-size: 14px;
  }

  .instructional-tooltip {
    /* Posicionamento calculado via JavaScript - sem transform */
    transform: none !important;
  }

  /* Remove transform em mobile - posicionamento é calculado no JS */
  .position-right,
  .position-left,
  .position-top,
  .position-bottom {
    transform: none !important;
  }

  .tooltip-arrow {
    display: none !important;
  }

  /* Ajustes para texto menor em mobile */
  .tooltip-title {
    font-size: 15px;
  }

  .tooltip-body {
    font-size: 13px;
    line-height: 1.4;
  }

  .btn-understood {
    padding: 10px 24px;
    font-size: 15px;
  }
}
</style>
